/**
 * Keyboard shortcut system — mirrors HotKeysManager + HotKeysAction from GWT.
 *
 * Supports:
 *   - Default key bindings matching GWT defaults
 *   - User-customizable bindings (stored in preferences)
 *   - Modifier keys: Ctrl, Alt, Shift, Meta
 *   - Context-aware: worklist shortcuts vs viewer shortcuts
 */

/* ── Key combination ── */

export interface KeysCombination {
  key: string;          // KeyboardEvent.key value (e.g. 'Enter', 'a', 'F1')
  ctrl?: boolean;
  alt?: boolean;
  shift?: boolean;
  meta?: boolean;
}

/** Serialize a key combo to a display string */
export function formatKeyCombination(combo: KeysCombination): string {
  const parts: string[] = [];
  if (combo.ctrl) parts.push('Ctrl');
  if (combo.alt) parts.push('Alt');
  if (combo.shift) parts.push('Shift');
  if (combo.meta) parts.push('Meta');
  // Capitalize single-char keys
  const keyLabel = combo.key.length === 1 ? combo.key.toUpperCase() : combo.key;
  parts.push(keyLabel);
  return parts.join('+');
}

/** Check if a KeyboardEvent matches a KeysCombination */
export function matchesKeyCombination(event: KeyboardEvent, combo: KeysCombination): boolean {
  if (event.key.toLowerCase() !== combo.key.toLowerCase()) return false;
  if (!!combo.ctrl !== event.ctrlKey) return false;
  if (!!combo.alt !== event.altKey) return false;
  if (!!combo.shift !== event.shiftKey) return false;
  if (!!combo.meta !== event.metaKey) return false;
  return true;
}

/* ── Hotkey action categories ── */

export type HotKeyCategory =
  | 'worklist'
  | 'viewer'
  | 'navigation'
  | 'annotations'
  | 'window_level'
  | 'snapshots'
  | '3d';

/* ── Hotkey action definition ── */

export interface HotKeyAction {
  id: string;
  label: string;
  category: HotKeyCategory;
  defaultBinding: KeysCombination | null;
}

/* ── All hotkey actions (matching GWT HotKeysAction enum) ── */

export const HOT_KEY_ACTIONS: HotKeyAction[] = [
  // ── Worklist ──
  { id: 'REFRESH', label: 'Refresh', category: 'worklist', defaultBinding: { key: 'F5' } },
  { id: 'COMPLETE_STUDY', label: 'Complete Study', category: 'worklist', defaultBinding: null },
  { id: 'ASSIGN_STUDY', label: 'Assign Study', category: 'worklist', defaultBinding: null },
  { id: 'ASSIGN_TO_ME', label: 'Assign to Me', category: 'worklist', defaultBinding: null },
  { id: 'RELEASE_STUDY', label: 'Release Study', category: 'worklist', defaultBinding: null },
  { id: 'HOLD_STUDY', label: 'Hold Study', category: 'worklist', defaultBinding: null },
  { id: 'UNHOLD_STUDY', label: 'Unhold Study', category: 'worklist', defaultBinding: null },
  { id: 'URGENT_FINDINGS', label: 'Urgent Findings', category: 'worklist', defaultBinding: null },
  { id: 'IMPORTANT_FINDINGS', label: 'Important Findings', category: 'worklist', defaultBinding: null },
  { id: 'FOLLOW_UP_RECOMMENDED', label: 'Follow-up Recommended', category: 'worklist', defaultBinding: null },
  { id: 'DISCORDANCE', label: 'Discordance', category: 'worklist', defaultBinding: null },
  { id: 'SHARE_STUDY', label: 'Share Study', category: 'worklist', defaultBinding: null },
  { id: 'SEND_STUDY_TO', label: 'Send Study', category: 'worklist', defaultBinding: null },
  { id: 'DOWNLOAD_STUDY', label: 'Download Study', category: 'worklist', defaultBinding: null },
  { id: 'ADD_REPORT', label: 'Add Report', category: 'worklist', defaultBinding: null },
  { id: 'SHOW_STUDY_NOTES', label: 'Show Study Notes', category: 'worklist', defaultBinding: null },
  { id: 'CONSULTATION', label: 'Consultation', category: 'worklist', defaultBinding: null },

  // ── Viewer toggle ──
  { id: 'STUDIES_VIEWER_HIDE_SHOW', label: 'Toggle Studies/Viewer', category: 'viewer', defaultBinding: { key: 'Escape' } },
  { id: 'CLOSE_STUDY', label: 'Close Study', category: 'viewer', defaultBinding: null },
  { id: 'TIMELINE_HIDE_SHOW', label: 'Toggle Timeline', category: 'viewer', defaultBinding: null },
  { id: 'THUMBNAILS_HIDE_SHOW', label: 'Toggle Thumbnails', category: 'viewer', defaultBinding: null },
  { id: 'REPORT_HIDE_SHOW', label: 'Toggle Report', category: 'viewer', defaultBinding: null },
  { id: 'DETAILS_HIDE_SHOW', label: 'Toggle Details', category: 'viewer', defaultBinding: null },
  { id: 'FULL_SCREEN_HIDE_SHOW', label: 'Toggle Full Screen', category: 'viewer', defaultBinding: { key: 'f', alt: true } },

  // ── Window/Level ──
  { id: 'WINDOW_LEVEL_FREEHAND_ON_OFF', label: 'W/L Freehand', category: 'window_level', defaultBinding: null },
  { id: 'WINDOW_LEVEL_INVERT_ON_OFF', label: 'Invert', category: 'window_level', defaultBinding: { key: 'i' } },
  { id: 'WINDOW_LEVEL_DEFAULT_ON', label: 'W/L Default', category: 'window_level', defaultBinding: null },
  { id: 'WINDOW_LEVEL_FULL_RANGE_ON', label: 'W/L Full Range', category: 'window_level', defaultBinding: null },
  { id: 'WINDOW_LEVEL_RECOMMENDED_ON', label: 'W/L Recommended', category: 'window_level', defaultBinding: null },
  { id: 'WINDOW_LEVEL_PRESET_1', label: 'W/L Preset 1', category: 'window_level', defaultBinding: { key: '1' } },
  { id: 'WINDOW_LEVEL_PRESET_2', label: 'W/L Preset 2', category: 'window_level', defaultBinding: { key: '2' } },
  { id: 'WINDOW_LEVEL_PRESET_3', label: 'W/L Preset 3', category: 'window_level', defaultBinding: { key: '3' } },
  { id: 'WINDOW_LEVEL_PRESET_4', label: 'W/L Preset 4', category: 'window_level', defaultBinding: { key: '4' } },
  { id: 'WINDOW_LEVEL_PRESET_5', label: 'W/L Preset 5', category: 'window_level', defaultBinding: { key: '5' } },

  // ── Image manipulation ──
  { id: 'ROTATE_CLOCKWISE', label: 'Rotate Clockwise', category: 'viewer', defaultBinding: null },
  { id: 'ROTATE_COUNTER_CLOCKWISE', label: 'Rotate Counter-clockwise', category: 'viewer', defaultBinding: null },
  { id: 'FLIP_HORIZONTALLY', label: 'Flip Horizontally', category: 'viewer', defaultBinding: null },
  { id: 'FLIP_VERTICALLY', label: 'Flip Vertically', category: 'viewer', defaultBinding: null },
  { id: 'PAN_IMAGE_ON_OFF', label: 'Pan Image', category: 'viewer', defaultBinding: null },
  { id: 'ZOOM_ON_POINT_ON_OFF', label: 'Zoom', category: 'viewer', defaultBinding: null },
  { id: 'FIT_TO_WINDOW', label: 'Fit to Window', category: 'viewer', defaultBinding: null },
  { id: 'LINK_SERIES', label: 'Link Series', category: 'viewer', defaultBinding: null },

  // ── Annotations ──
  { id: 'MEASURE_TOOL_ON_OFF', label: 'Measure', category: 'annotations', defaultBinding: null },
  { id: 'DRAW_ARROW_ON_OFF', label: 'Draw Arrow', category: 'annotations', defaultBinding: null },
  { id: 'ARROW_WITH_TEXT_ON_OFF', label: 'Arrow with Text', category: 'annotations', defaultBinding: null },
  { id: 'FREEHAND_ON_OFF', label: 'Freehand', category: 'annotations', defaultBinding: null },
  { id: 'RECTANGLE_ON_OFF', label: 'Rectangle', category: 'annotations', defaultBinding: null },
  { id: 'ELLIPSE_ON_OFF', label: 'Ellipse', category: 'annotations', defaultBinding: null },
  { id: 'POLYGON_ON_OFF', label: 'Polygon', category: 'annotations', defaultBinding: null },
  { id: 'TEXT_ON_OFF', label: 'Text Annotation', category: 'annotations', defaultBinding: null },
  { id: 'FREEHAND_ROI', label: 'Freehand ROI', category: 'annotations', defaultBinding: null },
  { id: 'RECTANGLE_ROI', label: 'Rectangle ROI', category: 'annotations', defaultBinding: null },
  { id: 'ELLIPSE_ROI', label: 'Ellipse ROI', category: 'annotations', defaultBinding: null },
  { id: 'POLYGON_ROI', label: 'Polygon ROI', category: 'annotations', defaultBinding: null },
  { id: 'ANGLE_ON_OFF', label: 'Angle', category: 'annotations', defaultBinding: null },
  { id: 'COBB_ON_OFF', label: 'Cobb Angle', category: 'annotations', defaultBinding: null },
  { id: 'DELETE_ALL_MARKUPS', label: 'Delete All Markups', category: 'annotations', defaultBinding: null },
  { id: 'SCOUT_LINES_HIDE_SHOW', label: 'Toggle Scout Lines', category: 'annotations', defaultBinding: null },
  { id: 'SYSTEM_MARKUPS_HIDE_SHOW', label: 'Toggle System Markups', category: 'annotations', defaultBinding: null },
  { id: 'USER_MARKUPS_HIDE_SHOW', label: 'Toggle User Markups', category: 'annotations', defaultBinding: null },
  { id: 'FILTER_ON_OFF', label: 'Toggle Filter', category: 'annotations', defaultBinding: null },

  // ── Navigation ──
  { id: 'NEXT_PAGE_SHOW', label: 'Next Page', category: 'navigation', defaultBinding: { key: 'ArrowRight' } },
  { id: 'PREVIOUS_PAGE_SHOW', label: 'Previous Page', category: 'navigation', defaultBinding: { key: 'ArrowLeft' } },
  { id: 'NEXT_IMAGE_SHOW', label: 'Next Image', category: 'navigation', defaultBinding: { key: 'ArrowDown' } },
  { id: 'PREVIOUS_IMAGE_SHOW', label: 'Previous Image', category: 'navigation', defaultBinding: { key: 'ArrowUp' } },
  { id: 'NEXT_SERIES_SHOW', label: 'Next Series', category: 'navigation', defaultBinding: { key: 'ArrowRight', ctrl: true } },
  { id: 'PREVIOUS_SERIES_SHOW', label: 'Previous Series', category: 'navigation', defaultBinding: { key: 'ArrowLeft', ctrl: true } },
  { id: 'CLONE_SERIES', label: 'Clone Series', category: 'navigation', defaultBinding: null },

  // ── Snapshots ──
  { id: 'ADD_TO_REPORT', label: 'Add to Report', category: 'snapshots', defaultBinding: null },
  { id: 'SNAPSHOT_ON_OFF', label: 'Snapshot', category: 'snapshots', defaultBinding: null },
  { id: 'SNAPSHOT_FOR_REPORT_ON_OFF', label: 'Snapshot for Report', category: 'snapshots', defaultBinding: null },
  { id: 'WHOLE_IMAGE_SNAPSHOT', label: 'Whole Image Snapshot', category: 'snapshots', defaultBinding: null },

  // ── 3D ──
  { id: 'ROTATE_VIEW_3D', label: 'Rotate 3D View', category: '3d', defaultBinding: null },
  { id: 'RESET_3D', label: 'Reset 3D', category: '3d', defaultBinding: null },
  { id: 'NEXT_SLAB_3D', label: 'Next Slab', category: '3d', defaultBinding: null },
  { id: 'PREVIOUS_SLAB_3D', label: 'Previous Slab', category: '3d', defaultBinding: null },
];

/* ── Hotkey bindings store ── */

export type HotKeyBindings = Map<string, KeysCombination>;

/** Build default bindings from action definitions */
export function getDefaultBindings(): HotKeyBindings {
  const bindings = new Map<string, KeysCombination>();
  for (const action of HOT_KEY_ACTIONS) {
    if (action.defaultBinding) {
      bindings.set(action.id, action.defaultBinding);
    }
  }
  return bindings;
}

/** Serialize bindings to a plain object for storage */
function bindingsToObject(bindings: HotKeyBindings): Record<string, KeysCombination> {
  const obj: Record<string, KeysCombination> = {};
  for (const [key, value] of bindings) {
    obj[key] = value;
  }
  return obj;
}

/** Deserialize a plain object to HotKeyBindings */
function objectToBindings(obj: Record<string, KeysCombination>): HotKeyBindings {
  return new Map(Object.entries(obj));
}

/** Load user-customized bindings from localStorage */
export function loadUserBindings(): HotKeyBindings {
  try {
    const stored = localStorage.getItem('brit_hotkeys');
    if (stored) {
      const parsed = JSON.parse(stored) as Record<string, KeysCombination>;
      return objectToBindings(parsed);
    }
  } catch {
    // Ignore parse errors
  }
  return getDefaultBindings();
}

/** Save user bindings to localStorage and optionally to backend */
export function saveUserBindings(bindings: HotKeyBindings): void {
  const obj = bindingsToObject(bindings);
  localStorage.setItem('brit_hotkeys', JSON.stringify(obj));

  // Also persist to backend via setjson (fire-and-forget)
  import('../../api/rpc').then(({ rpcCall }) => {
    rpcCall('setjson', { key: 'userHotkeys', value: obj }).catch(() => {
      // Backend save failed — localStorage is the primary store
    });
  });
}

/**
 * Load bindings from backend, falling back to localStorage.
 * Call this on login to sync server-saved bindings.
 */
export async function loadUserBindingsFromServer(): Promise<HotKeyBindings> {
  try {
    const { rpcCall } = await import('../../api/rpc');
    const result = await rpcCall<Record<string, KeysCombination> | null>('getjson', { key: 'userHotkeys' });
    if (result && typeof result === 'object' && Object.keys(result).length > 0) {
      const bindings = objectToBindings(result);
      // Sync to localStorage
      localStorage.setItem('brit_hotkeys', JSON.stringify(result));
      return bindings;
    }
  } catch {
    // Backend unavailable
  }
  return loadUserBindings();
}

/** Find actionId for a given keyboard event */
export function findActionForEvent(
  event: KeyboardEvent,
  bindings: HotKeyBindings,
): string | null {
  for (const [actionId, combo] of bindings) {
    if (matchesKeyCombination(event, combo)) {
      return actionId;
    }
  }
  return null;
}
